import Typography from "@mui/material/Typography";
import { ProofFileInput, acceptRules, IdentityPlaceholderTitle } from "ui/forms/formik-elements/FileInput";
import OnboardingForm from "../../OnboardingForm";
import { FormField, type OnboardingUploadDocumentsProps } from "./types";
import { DocumentIdentity } from "./entities";

const UploadDocuments = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingUploadDocumentsProps) => (
    <OnboardingForm
        {...form}
        isSubmitting={isSubmitting}
        isValid={isValid}
        isIncomplete={isIncomplete}
        requestedChangeFields={requestedChangeFields}
        handleSubmit={handleSubmit}
    >
        <Typography
            variant='h6'
        >
            Certificate of Incorporation
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.CertificateOfIncorporation}
            file={values[FormField.CertificateOfIncorporation][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.CertificateOfIncorporation,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
         <Typography
            marginTop={2}
            variant='h6'
        >
            Memorandum and Articles of Association
        </Typography>
        <Typography
            variant='caption'
        >
            (or official document that shows shareholders with percentage of shares)
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.MemorandumAndArticles}
            file={values[FormField.MemorandumAndArticles][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.MemorandumAndArticles,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Account Bank Statement
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.CompanyAccountBankStatement}
            file={values[FormField.CompanyAccountBankStatement][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.CompanyAccountBankStatement,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Financial Statement
        </Typography>
        <Typography
            variant='caption'
        >
            Previous fiscal year
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.CompanyFinancialStatement}
            file={values[FormField.CompanyFinancialStatement][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.CompanyFinancialStatement,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Bank Reference
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.BankReference}
            file={values[FormField.BankReference][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.BankReference,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Copy of your license(s)
        </Typography>
        <ProofFileInput
            multiple
            disabled={!isIncomplete}
            name={FormField.CopyOfLicences}
            files={values[FormField.CopyOfLicences]
                .map(doc => doc.documentFileKey)
                .filter(Boolean) as Array<File | string>}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(
                    key,
                    value.map((file: string | File) => new DocumentIdentity({
                        documentType: FormField.CopyOfLicences,
                        documentFileKey: file
                    })),
                    shouldValidate
                );
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            onRemove={file => {
                setFieldValue(
                    FormField.CopyOfLicences,
                    values[FormField.CopyOfLicences]
                        .filter(doc => doc.documentFileKey !== file),
                    true
                );
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Proof of Domain Ownership
        </Typography>
        <Typography
            variant='caption'
        >
            A monthly or yearly invoice or screenshot clearly showing ownership of each domain you wish to onboard
        </Typography>
        <ProofFileInput
            multiple
            disabled={!isIncomplete}
            name={FormField.ProofOfDomainOwnership}
            files={values[FormField.ProofOfDomainOwnership]
                .map(doc => doc.documentFileKey)
                .filter(Boolean) as Array<File | string>}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(
                    key,
                    value.map((file: string | File) => new DocumentIdentity({
                        documentType: FormField.ProofOfDomainOwnership,
                        documentFileKey: file
                    })),
                    shouldValidate
                );
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            onRemove={file => {
                setFieldValue(
                    FormField.ProofOfDomainOwnership,
                    values[FormField.ProofOfDomainOwnership]
                        .filter(doc => doc.documentFileKey !== file),
                    true
                );
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Official Company Documents filed with the authorities confirming latest Directorship and Shareholding of the company
        </Typography>
        <ProofFileInput
            multiple={false}
            disabled={!isIncomplete}
            name={FormField.OfficialCompanyDocWithAuthority}
            file={values[FormField.OfficialCompanyDocWithAuthority][0].documentFileKey}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(key, [new DocumentIdentity({
                    documentType: FormField.OfficialCompanyDocWithAuthority,
                    documentFileKey: value
                })], shouldValidate);
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company structure chart / diagram
        </Typography>
        <Typography
            variant='caption'
        >
            Where the structure of the company involves three or more companies, please provide clear chart/diagram along with the same documents, as stated above, for each company and shareholder involved in the structure.
        </Typography>
        <ProofFileInput
            multiple
            disabled={!isIncomplete}
            name={FormField.StructureChart}
            files={values[FormField.StructureChart]
                .map(doc => doc.documentFileKey)
                .filter(Boolean) as Array<File | string>}
            setFieldValue={(key, value, shouldValidate) => {
                setFieldValue(
                    key,
                    value.map((file: string | File) => new DocumentIdentity({
                        documentType: FormField.StructureChart,
                        documentFileKey: file
                    })),
                    shouldValidate
                );
            }}
            setFieldTouched={(key, touched, shouldValidate) => {
                setFieldTouched(key, touched, shouldValidate);
            }}
            onRemove={file => {
                setFieldValue(
                    FormField.StructureChart,
                    values[FormField.StructureChart]
                        .filter(doc => doc.documentFileKey !== file),
                    true
                );
            }}
            placeholderProps={{
                titleSlot: (
                    <IdentityPlaceholderTitle />
                )
            }}
            accept={acceptRules.identity}
        />
        {/* <Typography
            variant='h6'
            color='error'
        >
            "Copy of your license(s)", "Proof of Domain Ownership", "Company structure chart / diagram"
            require multifile upload capability!!!
        </Typography>
        <Typography
            variant='h6'
        >
            Certificate of Incorporation
        </Typography>
        <OnboardingInputControl
            name={FormField.CertificateOfIncorporation}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CertificateOfIncorporation}
                    file={values[FormField.CertificateOfIncorporation]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Memorandum and Articles of Association
        </Typography>
        <Typography
            variant='caption'
        >
            (or official document that shows shareholders with percentage of shares)
        </Typography>
        <OnboardingInputControl
            name={FormField.MemorandumAndArticlesFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.MemorandumAndArticlesFileKey}
                    file={values[FormField.MemorandumAndArticlesFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Account Bank Statement
        </Typography>
        <OnboardingInputControl
            name={FormField.CompanyAccountBankStatementFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CompanyAccountBankStatementFileKey}
                    file={values[FormField.CompanyAccountBankStatementFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company Financial Statement
        </Typography>
        <Typography
            variant='caption'
        >
            Previous fiscal year
        </Typography>
        <OnboardingInputControl
            name={FormField.CompanyFinancialStatementFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CompanyFinancialStatementFileKey}
                    file={values[FormField.CompanyFinancialStatementFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Bank Reference
        </Typography>
        <OnboardingInputControl
            name={FormField.BankReferenceFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.BankReferenceFileKey}
                    file={values[FormField.BankReferenceFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Copy of your license(s)
        </Typography>
        <OnboardingInputControl
            name={FormField.CopyOfLicencesFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.CopyOfLicencesFileKey}
                    file={values[FormField.CopyOfLicencesFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Proof of Domain Ownership
        </Typography>
        <Typography
            variant='caption'
        >
            A monthly or yearly invoice or screenshot clearly showing ownership of each domain you wish to onboard
        </Typography>
        <OnboardingInputControl
            name={FormField.ProofOfDomainOwnershipFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.ProofOfDomainOwnershipFileKey}
                    file={values[FormField.ProofOfDomainOwnershipFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Official Company Documents filed with the authorities confirming latest Directorship and Shareholding of the company
        </Typography>
        <OnboardingInputControl
            name={FormField.OfficialCompanyDocWithAuthorityFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.OfficialCompanyDocWithAuthorityFileKey}
                    file={values[FormField.OfficialCompanyDocWithAuthorityFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        />
        <Typography
            marginTop={2}
            variant='h6'
        >
            Company structure chart / diagram
        </Typography>
        <Typography
            variant='caption'
        >
            Where the structure of the company involves three or more companies, please provide clear chart/diagram along with the same documents, as stated above, for each company and shareholder involved in the structure.
        </Typography>
        <OnboardingInputControl
            name={FormField.StructureChartFileKey}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            renderInput={({ disabled }) => (
                <ProofFileInput
                    multiple={false}
                    disabled={disabled}
                    name={FormField.StructureChartFileKey}
                    file={values[FormField.StructureChartFileKey]}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    placeholderProps={{
                        titleSlot: (
                            <IdentityPlaceholderTitle />
                        )
                    }}
                    accept={acceptRules.identity}
                />
            )}
        /> */}
    </OnboardingForm>
);

export default UploadDocuments;
